import React, { useEffect, useState, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";

import Layout from "./components/Layout";
import SignIn from "./components/SignIn";
import OrdersTab from "./components/OrdersTab";

const isTokenValid = () => {
  const token = localStorage.getItem("admin_token");
  const expiresAt = localStorage.getItem("admin_expiresAt");

  if (!token || !expiresAt) return false;

  const expiryTime = new Date(expiresAt).getTime();

  if (isNaN(expiryTime)) return false;

  return Date.now() < expiryTime;
};

function ProtectedRoute({ isAuthenticated }) {
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

function AdminLayout({ username, role, onLogout }) {
  return (
    <Layout username={username} role={role} onLogout={onLogout}>
      <Outlet />
    </Layout>
  );
}

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isAuthenticated, setIsAuthenticated] = useState(isTokenValid());
  const [username, setUsername] = useState(
    localStorage.getItem("admin_username") || ""
  );
  const [role, setRole] = useState(
    localStorage.getItem("admin_role") || "admin"
  );

  const handleSignIn = useCallback(
    (user, userRole = "admin") => {
      setIsAuthenticated(true);
      setUsername(user);
      setRole(userRole);

      navigate("/Home", { replace: true });
    },
    [navigate]
  );

  const handleLogout = useCallback(() => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_expiresAt");
    localStorage.removeItem("admin_username");
    localStorage.removeItem("admin_role");
    sessionStorage.removeItem("admin_activeTab");

    setIsAuthenticated(false);
    setUsername("");
    setRole("admin");

    navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const valid = isTokenValid();

    if (!valid) {
      setIsAuthenticated(false);
      setUsername("");
      setRole("admin");
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      if (!isTokenValid()) {
        handleLogout();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, handleLogout]);

  useEffect(() => {
    if (isAuthenticated && location.pathname === "/") {
      navigate("/Home", { replace: true });
    }
  }, [isAuthenticated, location.pathname, navigate]);

  return (
    <Routes>
      <Route path="/" element={<SignIn onSignIn={handleSignIn} />} />

      <Route element={<ProtectedRoute isAuthenticated={isAuthenticated} />}>
        <Route
          path="/Home"
          element={
            <AdminLayout
              username={username}
              role={role}
              onLogout={handleLogout}
            />
          }
        >
          <Route index element={<OrdersTab role={role} />} />
        </Route>
      </Route>

      <Route
        path="*"
        element={<Navigate to={isAuthenticated ? "/Home" : "/"} replace />}
      />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}